import React, { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar, ExternalLink } from 'lucide-react';
import { getBookingsUrl, isBookingsConfigured } from '@/lib/zohoConfig';
import { fireEvent } from '@/lib/analytics';

interface ZohoBookingButtonProps {
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
  size?: 'default' | 'sm' | 'lg';
  domain?: string;
  className?: string;
  trackView?: boolean; // Whether to track booking_view event
}

export default function ZohoBookingButton({
  variant = 'default',
  size = 'default',
  domain,
  className,
  trackView = false,
}: ZohoBookingButtonProps) {
  const hasTrackedView = useRef(false);

  // Track view event once per component mount (if enabled)
  useEffect(() => {
    if (trackView && !hasTrackedView.current && domain) {
      fireEvent('booking_view', { domain });
      hasTrackedView.current = true;
    }
  }, [trackView, domain]);

  const handleBookingClick = () => {
    // Track click analytics
    fireEvent('booking_click', {
      domain: domain || null,
    });

    // Open Zoho Bookings
    const bookingsUrl = getBookingsUrl();
    if (bookingsUrl) {
      window.open(bookingsUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Don't render if not configured
  if (!isBookingsConfigured()) {
    return null;
  }

  return (
    <Button variant={variant} size={size} onClick={handleBookingClick} className={className}>
      <Calendar className="w-4 h-4 mr-2" />
      Book Free Call
      <ExternalLink className="w-4 h-4 ml-2" />
    </Button>
  );
}
